import React, {useState, useEffect, useContext, useCallback, useRef} from 'react';
import axios from 'axios';
import { API_URL } from '../../constants';
import {Link, useNavigate, useParams} from 'react-router-dom';
import withAuth from "../../auth";
import { Breadcrumb } from 'react-bootstrap';
import moment from "moment";
import {AuthContext} from "../AuthContext";
import CreditCardModal from "./CreditCardModal";
import Spinner from "react-bootstrap/Spinner";
import NPMapViewDirections from "../google_map/NPMapViewDirections";
import { GoogleMapsProvider } from '../google_map/GoogleMapsContext';
import {formatPhoneNumberWithMask} from "../../formatPhoneNumber";
import echo from "../../echo";
import PickupBillingDocuments from "./PickupBillingDocuments";
import PickupDeleteAction from "./PickupDeleteAction";
import PickupCancellationFeeBadge from "./PickupCancellationFeeBadge";
import WhitelabelBadge from "../WhitelabelBadge";
import {
    formatLocationDisplay,
    formatPickupDropoffDisplay,
    formatPickupOriginDisplay,
} from '../../utils/locationDisplay';
import {
    formatAllowedPaymentMethods,
    isPaymentMethodAllowed,
    isPaymentMethodAvailable,
} from '../users/billingSettings';

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';

const CARD_BRAND_LABELS = {
    amex: 'AMEX',
    cartes_bancaires: 'CARTES BANCAIRES',
    diners: 'DINERS CLUB',
    discover: 'DISCOVER',
    eftpos_au: 'EFTPOS AU',
    interac: 'INTERAC',
    jcb: 'JCB',
    link: 'LINK',
    mastercard: 'MASTERCARD',
    unionpay: 'UNIONPAY',
    visa: 'VISA',
};

const getPaymentMethodDetails = (pickup, paymentType) => {
    const details = pickup?.payment_method_details;

    if (!details || typeof details !== 'object' || Array.isArray(details)) return null;

    const detailsType = String(details.type || '').trim().toLowerCase();

    return !detailsType || detailsType === paymentType ? details : null;
};

const getCardDetailsLabel = (details) => {
    const brand = String(details?.card?.brand || '').trim().toLowerCase();
    const last4 = String(details?.card?.last4 || '').trim();
    const brandLabel = CARD_BRAND_LABELS[brand];

    return brandLabel && /^\d{4}$/.test(last4)
        ? `${brandLabel} **** ${last4}`
        : null;
};

const getInvoiceTermsLabel = (details) => {
    const terms = String(details?.invoice?.terms || '').trim().toLowerCase();

    if (terms === 'upon_receipt') return 'Due upon receipt';

    const netDays = terms.match(/^net_(\d{1,3})$/)?.[1];

    return netDays && Number(netDays) > 0 ? `Net ${Number(netDays)}` : null;
};

const getUsedPaymentMethodLabel = (pickup) => {
    const declaredPaymentType = String(pickup?.payment_type || '').trim().toLowerCase();
    const detailsPaymentType = String(pickup?.payment_method_details?.type || '').trim().toLowerCase();
    const paymentType = declaredPaymentType || detailsPaymentType;

    if (paymentType === 'invoice') {
        const termsLabel = getInvoiceTermsLabel(getPaymentMethodDetails(pickup, paymentType));

        return termsLabel ? `Invoice / Terms — ${termsLabel}` : 'Invoice / Terms';
    }

    if (paymentType === 'card') {
        const cardLabel = getCardDetailsLabel(getPaymentMethodDetails(pickup, paymentType));

        return cardLabel ? `Credit Card — ${cardLabel}` : 'Credit Card';
    }

    if (
        !paymentType
        && (hasValue(pickup?.payment_method_id) || hasValue(pickup?.payment_intent_id))
    ) {
        return 'Credit Card';
    }

    return null;
};

const PickupDetails = () => {
    const { pickupId } = useParams();
    const [pickup, setPickup] = useState(null);
    const [trackers, setTrackers] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const {token, setToken, hasPermission} = useContext(AuthContext);
    const canUpdatePickup = hasPermission('pickup-update');
    const canManageCustomerPaymentMethods = hasPermission('user-update');
    const [showModal, setShowModal] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [billingSettings, setBillingSettings] = useState(null);
    const [billingSettingsContextKey, setBillingSettingsContextKey] = useState(null);
    const [isBillingSettingsLoading, setIsBillingSettingsLoading] = useState(false);
    const [billingSettingsError, setBillingSettingsError] = useState('');
    const billingSettingsRequestIdRef = useRef(0);
    const invoiceBookingRequestIdRef = useRef(0);
    const currentInvoiceBookingContextRef = useRef(null);
    const lastInvoiceBookingContextKeyRef = useRef(null);
    const activeInvoiceBookingRequestRef = useRef(null);
    const [invoiceBookingSubmission, setInvoiceBookingSubmission] = useState(null);
    const [isCancelSubmitted, setIsCancelSubmitted] = useState(false);
    const [isUnclaimSubmitted, setIsUnclaimSubmitted] = useState(false);
    const pickupDeliveryStatusId = Number(pickup?.delivery_status?.id);
    const isCurrentRoutePickup = Boolean(pickup?.id)
        && String(pickup.id) === String(pickupId);
    const isRebookableCanceledPickup = pickupDeliveryStatusId === 6
        && !pickup?.completed_at;
    const canShowBookingControls = canUpdatePickup
        && isCurrentRoutePickup
        && Boolean(pickup?.user?.id)
        && (
            isRebookableCanceledPickup
            || (pickupDeliveryStatusId === 0 && !pickup?.waiting_for_driver_at)
        );

    const [claimDriverId, setClaimDriverId] = useState(0);
    const [isClaimSubmitted, setIsClaimSubmitted] = useState(false);

    const [deliveryCost, setDeliveryCost] = useState(0.0);
    const [isDeliveryCostSubmitted, setDeliveryCostSubmitted] = useState(false);

    const [promoCode, setPromoCode] = useState('');
    const [isPromoSubmitting, setIsPromoSubmitting] = useState(false);

    const [adminNotes, setAdminNotes] = useState('');
    const [showAdminNotesForm, setShowAdminNotesForm] = useState(false);
    const [isAdminNotesSubmitting, setIsAdminNotesSubmitting] = useState(false);

    const [driverStatusSubmitting, setDriverStatusSubmitting] = useState(null);
    const [photoUploadSubmitting, setPhotoUploadSubmitting] = useState(null);

    const statusColors = {
        '0': 'transparent',
        '1': '#FFFAE5',  // Very Light Gold
        '2': '#FFCBB2',  // Very Light OrangeRed
        '3': '#CFF3C8',  // Very Light Green
        '4': '#C7D6E9',  // Very Light SteelBlue
        '5': '#D1F2CF',  // Very Light LimeGreen
        '6': '#FFA3B5'   // Very Light Crimson
    };

    const fetchPickup = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/admin/pickups/${pickupId}`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            setPickup(response.data.data);
            setTrackers(response.data.trackers);
        } catch (error) {
            console.error('Error fetching the pickup details!', error);
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
        }
    }, [navigate, pickupId, setToken, token]);

    useEffect(() => {
        fetchPickup();
    }, [fetchPickup]);

    const fetchPickupBillingOptions = useCallback(async (
        requestedPickupId,
        customerUserId
    ) => {
        if (!requestedPickupId || !customerUserId) return;

        const requestedContextKey = `${requestedPickupId}:${customerUserId}`;
        const requestId = ++billingSettingsRequestIdRef.current;

        setBillingSettings(null);
        setBillingSettingsContextKey(requestedContextKey);
        setBillingSettingsError('');
        setIsBillingSettingsLoading(true);

        try {
            const response = await axios.get(
                `${API_URL}/api/admin/pickups/${requestedPickupId}/billing-options`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (requestId !== billingSettingsRequestIdRef.current) return;

            const settings = response.data?.billing_options;
            if (
                !settings
                || !Array.isArray(settings.allowed_payment_methods)
                || typeof settings.invoice_payment_allowed !== 'boolean'
                || typeof settings.card_payment_allowed !== 'boolean'
            ) {
                throw new Error('The customer Billing Settings response was invalid.');
            }

            setBillingSettings(settings);
        } catch (requestError) {
            if (requestId !== billingSettingsRequestIdRef.current) return;

            if (requestError.response && requestError.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }

            setBillingSettingsError(
                requestError?.response?.data?.message
                || requestError.message
                || 'Unable to load customer Billing Settings.'
            );
        } finally {
            if (requestId === billingSettingsRequestIdRef.current) {
                setIsBillingSettingsLoading(false);
            }
        }
    }, [navigate, setToken, token]);

    useEffect(() => {
        const requestedPickupId = pickup?.id;
        const customerUserId = pickup?.user?.id;

        if (!canShowBookingControls || !requestedPickupId || !customerUserId) {
            billingSettingsRequestIdRef.current += 1;
            setBillingSettings(null);
            setBillingSettingsContextKey(null);
            setBillingSettingsError('');
            setIsBillingSettingsLoading(false);
            setShowModal(false);
            return undefined;
        }

        fetchPickupBillingOptions(requestedPickupId, customerUserId);

        return () => {
            billingSettingsRequestIdRef.current += 1;
        };
    }, [
        canShowBookingControls,
        fetchPickupBillingOptions,
        pickup?.id,
        pickup?.user?.id,
    ]);

    const currentBillingSettingsLoaded = Boolean(
        billingSettings
        && pickup?.id
        && pickup?.user?.id
        && billingSettingsContextKey === `${pickup.id}:${pickup.user.id}`
    );
    const cardPaymentConfigured = currentBillingSettingsLoaded
        && isPaymentMethodAllowed(billingSettings, 'card');
    const invoicePaymentConfigured = currentBillingSettingsLoaded
        && isPaymentMethodAllowed(billingSettings, 'invoice');
    const cardPaymentAllowed = currentBillingSettingsLoaded
        && isPaymentMethodAvailable(billingSettings, 'card');
    const invoicePaymentAllowed = currentBillingSettingsLoaded
        && isPaymentMethodAvailable(billingSettings, 'invoice');
    const cardBookingAllowed = cardPaymentAllowed && canManageCustomerPaymentMethods;
    const invoiceBookingContextKey = JSON.stringify([
        pickupId ?? null,
        pickup?.id ?? null,
        pickup?.user?.id ?? null,
        canShowBookingControls,
        Boolean(invoicePaymentAllowed),
    ]);
    if (lastInvoiceBookingContextKeyRef.current !== invoiceBookingContextKey) {
        lastInvoiceBookingContextKeyRef.current = invoiceBookingContextKey;
        invoiceBookingRequestIdRef.current += 1;
    }
    const isInvoiceBookSubmitted = Boolean(
        invoiceBookingSubmission
        && invoiceBookingSubmission.requestId === invoiceBookingRequestIdRef.current
        && invoiceBookingSubmission.contextKey === invoiceBookingContextKey
    );
    currentInvoiceBookingContextRef.current = {
        contextKey: invoiceBookingContextKey,
        routePickupId: pickupId,
        pickupId: pickup?.id,
        userId: pickup?.user?.id,
        canShowBookingControls,
        invoicePaymentAllowed,
    };

    useEffect(() => {
        return () => {
            invoiceBookingRequestIdRef.current += 1;
            activeInvoiceBookingRequestRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!cardBookingAllowed) setShowModal(false);
    }, [cardBookingAllowed]);

    useEffect(() => {
        const channel = pickup?.channel_id;
        if (!channel) return undefined;

        // Laravel-Echo-Server
        const subscription = echo.channel(channel);

        subscription.listen('.App\\Events\\Admin\\PickupUpdated', (event) => {
            if (!event.pickup || String(event.pickup.id) !== String(pickupId)) return;

            setPickup((prevPickup) => ({
                ...prevPickup,
                ...event.pickup,
            }));

            if (event.trackers !== undefined) {
                setTrackers(event.trackers);
            }

            // Photo URLs also live inside item origins/dropoffs. Refetch the
            // canonical detail response so those nested resources stay fresh.
            fetchPickup();
        });

        const socket = echo?.connector?.socket;
        const handleReconnect = () => fetchPickup();
        socket?.on?.('connect', handleReconnect);

        return () => {
            if (typeof socket?.off === 'function') {
                socket.off('connect', handleReconnect);
            } else {
                socket?.removeListener?.('connect', handleReconnect);
            }
            echo.leave(channel);
        };
    }, [fetchPickup, pickup?.channel_id, pickupId]);

    useEffect(() => {
        setDeliveryCost(pickup?.delivery_cost || 0.0);
        setAdminNotes(pickup?.admin_notes || '');
    }, [pickup]);

    const handleModalShow = () => {
        if (cardBookingAllowed) setShowModal(true);
    };
    const handleModalClose = () => setShowModal(false);

    const handleModalSuccess = (msg, pickup) => {
        setSuccess(msg);
        setPickup(pickup);
    };

    const bookPickupWithInvoice = async () => {
        if (
            isInvoiceBookSubmitted
            || (
                activeInvoiceBookingRequestRef.current?.requestId
                    === invoiceBookingRequestIdRef.current
                && activeInvoiceBookingRequestRef.current?.contextKey
                    === invoiceBookingContextKey
            )
        ) return;
        if (
            !invoicePaymentAllowed
            || !canShowBookingControls
            || !pickup?.id
            || !pickup?.user?.id
        ) return;

        if (!window.confirm(
            'Book this delivery on behalf of the customer using Invoice / Terms?'
        )) return;

        const requestId = ++invoiceBookingRequestIdRef.current;
        const requestedRoutePickupId = pickupId;
        const requestedPickupId = pickup.id;
        const requestedUserId = pickup.user.id;
        const requestedContextKey = invoiceBookingContextKey;
        const bookingRequest = {requestId, contextKey: requestedContextKey};
        activeInvoiceBookingRequestRef.current = bookingRequest;
        setInvoiceBookingSubmission(bookingRequest);
        setSuccess('');
        setError('');

        try {
            const response = await axios.post(
                `${API_URL}/api/admin/pickups/${requestedPickupId}/waiting-for-driver`,
                {payment_type: 'invoice'},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const current = currentInvoiceBookingContextRef.current;
            if (
                requestId !== invoiceBookingRequestIdRef.current
                || !current?.canShowBookingControls
                || !current?.invoicePaymentAllowed
                || current.contextKey !== requestedContextKey
                || String(current.routePickupId) !== String(requestedRoutePickupId)
                || String(current.pickupId) !== String(requestedPickupId)
                || String(current.userId) !== String(requestedUserId)
            ) return;

            setSuccess(
                `The delivery ${requestedPickupId} has been booked with Invoice / Terms.`
            );
            setPickup(response.data.pickup);
        } catch (requestError) {
            const current = currentInvoiceBookingContextRef.current;
            if (
                requestId !== invoiceBookingRequestIdRef.current
                || !current?.canShowBookingControls
                || !current?.invoicePaymentAllowed
                || current.contextKey !== requestedContextKey
                || String(current.routePickupId) !== String(requestedRoutePickupId)
                || String(current.pickupId) !== String(requestedPickupId)
                || String(current.userId) !== String(requestedUserId)
            ) return;

            if (requestError.response && requestError.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }

            if (requestError?.response?.data?.errors) {
                const errors = requestError.response.data.errors;
                setError(
                    Object.keys(errors)
                        .map((key) => errors[key].join(' '))
                        .join(' ')
                );
            } else {
                setError(
                    requestError?.response?.data?.message
                    || 'Unable to book this delivery with Invoice / Terms.'
                );
            }
        } finally {
            const current = currentInvoiceBookingContextRef.current;
            if (
                requestId === invoiceBookingRequestIdRef.current
                && current?.canShowBookingControls
                && current?.invoicePaymentAllowed
                && current.contextKey === requestedContextKey
                && String(current.routePickupId) === String(requestedRoutePickupId)
                && String(current.pickupId) === String(requestedPickupId)
                && String(current.userId) === String(requestedUserId)
            ) {
                activeInvoiceBookingRequestRef.current = null;
                setInvoiceBookingSubmission(null);
            }
        }
    };

    const cancelPickup = async () => {
        const confirmation = Number(pickup.delivery_status?.id) === 5
            ? "This delivery is completed. Canceling it will override the completed status. Continue?"
            : "Are you sure that you want to cancel this delivery?";

        if (!window.confirm(confirmation)) return false;

        setIsCancelSubmitted(true);
        try {
            const response = await axios.post(`${API_URL}/api/admin/pickups/${pickup.id}/cancel`, {}, {
                headers: {'Authorization': `Bearer ${token}`}
            });
            setSuccess(response.data.message);
            setPickup(response.data.pickup);
        }
        catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                setError(errorMessages);
            } else if (error?.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            setIsCancelSubmitted(false);
        }
    };

    const claimPickup = async () => {
        if (!window.confirm("Are you sure that you want to claim this delivery on behalf of the driver?")) return false;

        setIsClaimSubmitted(true);
        try {
            const response = await axios.post(`${API_URL}/api/admin/pickups/${pickup.id}/claim`,
                {driver_id: claimDriverId}, {
                headers: {'Authorization': `Bearer ${token}`}
            });
            setSuccess(response.data.message);
            setPickup(response.data.pickup);
        }
        catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                setError(errorMessages);
            } else if (error?.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            setIsClaimSubmitted(false);
        }
    };

    const unclaimPickup = async () => {
        if (!window.confirm("Are you sure that you want to unclaim this delivery from the current driver?")) return false;

        setIsUnclaimSubmitted(true);
        try {
            const response = await axios.post(`${API_URL}/api/admin/pickups/${pickup.id}/unclaim`, {}, {
                headers: {'Authorization': `Bearer ${token}`}
            });
            setSuccess(response.data.message);
            setPickup(response.data.pickup);
        }
        catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                setError(errorMessages);
            } else if (error?.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            setIsUnclaimSubmitted(false);
        }
    };

    const changeDriverStatus = async (action, trackerId = null) => {
        const labels = {
            start: 'start this delivery on behalf of the driver',
            tracker: 'complete this tracker on behalf of the driver',
            complete: 'complete this delivery on behalf of the driver',
        };

        if (!window.confirm(`Are you sure that you want to ${labels[action]}?`)) return false;

        const submitKey = trackerId ? `${action}-${trackerId}` : action;
        setDriverStatusSubmitting(submitKey);
        setSuccess('');
        setError('');

        try {
            const response = await axios.post(
                `${API_URL}/api/admin/pickups/${pickup.id}/driver-status`,
                {
                    action,
                    tracker_id: trackerId,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setSuccess(response.data.message);

            if (response.data.pickup) {
                setPickup(response.data.pickup);
            }

            if (response.data.trackers) {
                setTrackers(response.data.trackers);
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }

            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors)
                    .map(key => errors[key].join(' '))
                    .join(' ');
                setError(errorMessages);
            } else if (error?.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            setDriverStatusSubmitting(null);
        }
    };

    const uploadAdminPickupPhoto = async (type, locationId, file) => {
        if (!file) return;

        const isSecured = type === 'secured';
        const submitKey = `${type}-${locationId || 'pickup'}`;

        const formData = new FormData();

        if (isSecured) {
            formData.append('secured_item_upload', file);
        } else {
            formData.append('complete_pickup_upload', file);
        }

        setPhotoUploadSubmitting(submitKey);
        setSuccess('');
        setError('');

        try {
            const endpoint = isSecured
                ? `${API_URL}/api/admin/pickups/${pickup.id}/secured-item/${locationId || ''}`
                : `${API_URL}/api/admin/pickups/${pickup.id}/complete-photo/${locationId || ''}`;

            const response = await axios.post(endpoint, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            setSuccess(response.data.message);

            if (response.data.pickup) {
                setPickup(response.data.pickup);
            }

            if (response.data.trackers) {
                setTrackers(response.data.trackers);
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }

            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors)
                    .map(key => errors[key].join(' '))
                    .join(' ');
                setError(errorMessages);
            } else if (error?.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            setPhotoUploadSubmitting(null);
        }
    };

    const saveDeliveryCost = async () => {
        if (!window.confirm("Are you sure that you want to change the delivery cost of this delivery?")) return false;

        setDeliveryCostSubmitted(true);
        try {
            const response = await axios.post(`${API_URL}/api/admin/pickups/${pickup.id}/change-cost`, {cost: deliveryCost}, {
                headers: {'Authorization': `Bearer ${token}`}
            });
            setSuccess(response.data.message);
            setPickup(response.data.pickup);
        }
        catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                setError(errorMessages);
            } else if (error?.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            setDeliveryCostSubmitted(false);
        }
    };

    const saveAdminNotes = async () => {
        setIsAdminNotesSubmitting(true);
        setSuccess('');
        setError('');

        try {
            const response = await axios.post(
                `${API_URL}/api/admin/pickups/${pickup.id}/admin-notes`,
                {admin_notes: adminNotes},
                {headers: {'Authorization': `Bearer ${token}`}}
            );

            setSuccess(response.data.message);
            setPickup(response.data.pickup);
            setShowAdminNotesForm(false);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }

            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                setError(errorMessages);
            } else if (error?.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            setIsAdminNotesSubmitting(false);
        }
    };

    const editAdminNotes = () => {
        setAdminNotes(pickup?.admin_notes || '');
        setShowAdminNotesForm(true);
    };

    const cancelAdminNotesEdit = () => {
        setAdminNotes(pickup?.admin_notes || '');
        setShowAdminNotesForm(false);
    };

    const applyPromoCode = async () => {
        if (!window.confirm("Are you sure that you want to apply this promo code?")) return false;

        setIsPromoSubmitting(true);
        try {
            const response = await axios.post(
                `${API_URL}/api/admin/pickups/${pickup.id}/apply-promo`,
                {promo_code: promoCode},
                { headers: {'Authorization': `Bearer ${token}`}}
            );
            setSuccess(response.data.message);
            setPickup(response.data.pickup);
            setPromoCode('');
        } catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                setError(errorMessages);
            } else if (error?.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            setIsPromoSubmitting(false);
        }
    };

    // Function to render stars based on rating
    const renderStars = (rating) => {
        const fullStars = Math.floor(rating);
        const halfStar = rating - fullStars >= 0.5;
        const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

        return (
            <>
                {[...Array(fullStars)].map((e, i) =>
                    <i key={`full-${i}`} className="fa fa-star" style={{ color: '#FFD700' }}></i>
                )}
                {halfStar && <i className="fa fa-star-half-o" style={{ color: '#FFD700' }}></i>}
                {[...Array(emptyStars)].map((e, i) =>
                    <i key={`empty-${i}`} className="fa fa-star-o" style={{ color: '#FFD700' }}></i>
                )}
                &nbsp;({rating.toFixed(1)})
            </>
        );
    };

    const canChangeDriverStatus =
        hasPermission('pickup-update') &&
            pickup?.driver &&
            pickup?.delivery_status?.id !== 5 &&
            pickup?.delivery_status?.id !== 6;

    const incompleteTrackers = trackers
        ? trackers.filter(tracker => !tracker.completed)
        : [];

    const isAtPickupTracker = (tracker) => {
        const title = (tracker.action_title || '').toLowerCase();

        return title.includes('at pickup');
    };

    const isAtDropoffTracker = (tracker) => {
        const title = (tracker.action_title || '').toLowerCase();

        return title.includes('at drop') || title.includes('at drop-off') || title.includes('at dropoff');
    };

    const getTrackerLocationId = (tracker, type) => {
        if (type === 'secured') {
            return tracker.origin?.id || null;
        }

        return tracker.dropoff?.id || null;
    };

    const allRequiredCompletePhotosUploaded = () => {
        if (!trackers || trackers.length === 0) return false;

        const dropoffTrackers = trackers.filter(tracker => isAtDropoffTracker(tracker));

        if (dropoffTrackers.length === 0) {
            return !!pickup?.complete_photo;
        }

        return dropoffTrackers.every(tracker =>
            !!tracker.dropoff?.photo_url
        );
    };

    const canShowCompletePickupButton =
        canChangeDriverStatus &&
        pickup?.started_at &&
        incompleteTrackers.length === 0 &&
        !pickup?.completed_at &&
        allRequiredCompletePhotosUploaded();

    const formatTimestamp = (value) => {
        return value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : '---';
    };

    const toAmount = (value) => Number.parseFloat(value || 0);
    const showRegularDeliveryCost = pickup && (
        pickup.is_promo_applied ||
        toAmount(pickup.regular_delivery_cost) !== toAmount(pickup.delivery_cost)
    );
    const driverEarningsPickupFee = pickup?.is_promo_applied
        ? pickup?.regular_delivery_cost
        : pickup?.delivery_cost;
    const shopifyFields = pickup ? [
        {label: 'Shopify Order ID', value: pickup.shopify_order_id},
        {label: 'Shopify Order Number', value: pickup.shopify_order_number},
        {label: 'Shopify Shop Domain', value: pickup.shopify_shop_domain},
    ].filter((field) => hasValue(field.value)) : [];
    const canEditAdminNotes = hasPermission('pickup-update');
    const adminNotesChanged = adminNotes !== (pickup?.admin_notes || '');
    const availableBookingPaymentLabels = [
        invoicePaymentAllowed ? 'Invoice / Terms' : null,
        cardBookingAllowed ? 'Credit Card' : null,
    ].filter(Boolean);
    const configuredButUnavailableLabels = [
        invoicePaymentConfigured && !invoicePaymentAllowed ? 'Invoice / Terms' : null,
        cardPaymentConfigured && !cardPaymentAllowed ? 'Credit Card' : null,
    ].filter(Boolean);
    const billingPolicyPending = canShowBookingControls
        && !currentBillingSettingsLoaded
        && !billingSettingsError;
    const usedPaymentMethodLabel = getUsedPaymentMethodLabel(pickup);

    return (
        <div className="container-fluid mt-3">
            <Breadcrumb>
                <Breadcrumb.Item href="/pickups">Deliveries</Breadcrumb.Item>
                <Breadcrumb.Item active>Delivery Details</Breadcrumb.Item>
            </Breadcrumb>
            {pickup ? (
                <div>
                    <h1 className="text-center" style={{position: 'relative'}}>
                        Delivery Details #{pickup ? pickup.id : ''}
                        {pickup.user && hasPermission('user-update') && (
                            <div className="duplicate-pickup-wrap">
                                <Link to={`/pickups/create/${pickup.user.id}?duplicate=${pickup.id}`}
                                      className='duplicate-pickup-btn btn btn-outline-primary'>
                                    Duplicate
                                </Link>

                            </div>
                        )}
                    </h1>
                    <div className="text-center mb-2">
                        <WhitelabelBadge whitelabelId={pickup.whitelabel_id}/>
                    </div>
                    {hasPermission('pickup-update') && pickup.delivery_status?.id !== 5 &&
                        <div className="d-flex justify-content-center mb-2">
                            <Link to={`/pickups/update/${pickup.id}`}  className='duplicate-pickup-btn btn btn-warning'>
                                Edit Delivery Details
                            </Link>
                        </div>
                    }
                    {success && <div className="alert alert-success">{success}</div>}
                    {error && <div className="alert alert-danger">{error}</div>}
                    <div className="row">
                        <div className="col-md-6">
                            <div className="card mb-2">
                                <div className="card-body" align="center">
                                    <h6>ID: {pickup.id}</h6>
                                    {pickup.delivery_status?.id !== 5 && pickup.delivery_status?.id !== 6 && pickup.waiting_for_driver_at && <h6 className="badge badge-success">Live Request</h6>}
                                    <h6 className="text-center p-2" style={{ backgroundColor: !pickup.delivery_status?.id ? 'initial' : statusColors[pickup.delivery_status?.id] || 'initial' }}>
                                        Delivery Status: {pickup.delivery_status?.status}
                                    </h6>
                                    {pickup.canceled_with_fee === true && (
                                        <div className="mb-2">
                                            <PickupCancellationFeeBadge canceledWithFee />
                                        </div>
                                    )}
                                    {hasPermission("pickup-update") && (
                                        <>
                                            {canShowBookingControls && (
                                                <div className="mb-2">
                                                    {billingPolicyPending && (
                                                        <div className="alert alert-info py-2 mb-2" role="status">
                                                            <Spinner
                                                                as="span"
                                                                animation="border"
                                                                size="sm"
                                                                aria-hidden="true"
                                                            />
                                                            &nbsp;Loading customer Billing Settings...
                                                        </div>
                                                    )}

                                                    {!isBillingSettingsLoading && billingSettingsError && (
                                                        <div className="alert alert-danger py-2 mb-2" role="alert">
                                                            <div>{billingSettingsError}</div>
                                                            <div className="small mt-1">
                                                                Booking is disabled until the customer Billing Settings load.
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-dark mt-2"
                                                                onClick={() => fetchPickupBillingOptions(
                                                                    pickup.id,
                                                                    pickup.user.id
                                                                )}
                                                            >
                                                                Retry Billing Settings
                                                            </button>
                                                        </div>
                                                    )}

                                                    {!isBillingSettingsLoading
                                                        && !billingSettingsError
                                                        && currentBillingSettingsLoaded && (
                                                        <div className="border rounded p-2">
                                                            <div className="font-weight-bold">
                                                                Customer Billing Settings
                                                            </div>
                                                            <div className="small mb-2">
                                                                Configured payment methods:{' '}
                                                                <strong>
                                                                    {formatAllowedPaymentMethods(billingSettings)}
                                                                </strong>
                                                            </div>

                                                            {configuredButUnavailableLabels.length > 0 && (
                                                                <div className="alert alert-warning py-2 mb-2">
                                                                    Unavailable for this customer's tenant:{' '}
                                                                    <strong>
                                                                        {configuredButUnavailableLabels.join(' and ')}
                                                                    </strong>
                                                                </div>
                                                            )}

                                                            {cardPaymentAllowed
                                                                && !canManageCustomerPaymentMethods && (
                                                                <div className="alert alert-warning py-2 mb-2">
                                                                    Credit Card is enabled in the customer Billing Settings,
                                                                    but unavailable to your current role. User Update permission
                                                                    is required to access customer payment methods.
                                                                </div>
                                                            )}

                                                            {availableBookingPaymentLabels.length === 0 ? (
                                                                <div className="alert alert-warning py-2 mb-0">
                                                                    No configured payment method is currently available.
                                                                    Booking is disabled.
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <div className="small mb-2">
                                                                        Available for booking:{' '}
                                                                        <strong>
                                                                            {availableBookingPaymentLabels.join(' and ')}
                                                                        </strong>
                                                                    </div>
                                                                    <div className="d-flex flex-wrap justify-content-center">
                                                                        {invoicePaymentAllowed && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-success mb-2 mr-2"
                                                                                onClick={bookPickupWithInvoice}
                                                                                disabled={isInvoiceBookSubmitted}
                                                                            >
                                                                                {isInvoiceBookSubmitted ? (
                                                                                    <>
                                                                                        <Spinner
                                                                                            as="span"
                                                                                            animation="border"
                                                                                            size="sm"
                                                                                            role="status"
                                                                                            aria-hidden="true"
                                                                                        />
                                                                                        &nbsp;Booking with Invoice...
                                                                                    </>
                                                                                ) : 'Book on Behalf with Invoice / Terms'}
                                                                            </button>
                                                                        )}

                                                                        {cardBookingAllowed && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-warning mb-2"
                                                                                onClick={handleModalShow}
                                                                                disabled={isInvoiceBookSubmitted}
                                                                            >
                                                                                Book on Behalf with Credit Card
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    )}

                                                    {cardBookingAllowed && (
                                                        <CreditCardModal
                                                            userId={pickup.user.id}
                                                            pickupId={pickup.id}
                                                            show={showModal}
                                                            cardPaymentAllowed={cardBookingAllowed}
                                                            handleClose={handleModalClose}
                                                            handleSuccess={handleModalSuccess}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            {Number(pickup.delivery_status?.id) !== 6 && (
                                                <>
                                                    {" "}
                                                    <button
                                                        className="btn btn-danger mb-2"
                                                        disabled={isCancelSubmitted}
                                                        onClick={cancelPickup}
                                                    >
                                                        {isCancelSubmitted
                                                            ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />&nbsp;Canceling...</>
                                                            : "Cancel"
                                                        }
                                                    </button>
                                                </>
                                            )}
                                            <PickupDeleteAction
                                                canDelete={Boolean(pickup.can_delete)}
                                                pickupId={pickup.id}
                                                token={token}
                                                onDeleted={() => navigate('/pickups')}
                                                onError={setError}
                                                onConflict={() => setPickup((currentPickup) => ({
                                                    ...currentPickup,
                                                    can_delete: false,
                                                }))}
                                                onUnauthorized={() => {
                                                    localStorage.removeItem('authToken');
                                                    setToken(null);
                                                    navigate('/');
                                                }}
                                            />
                                        </>
                                    )}
                                    <p>
                                        Customer: {pickup.user &&
                                        <Link to={`/users/${pickup.user.id}`}>
                                            <b>{pickup.user.id} {pickup.user.full_name}</b> ({pickup.user.email})
                                        </Link>
                                        }
                                        <br/>
                                        Driver: {pickup.driver &&
                                        <Link to={`/drivers/${pickup.driver.id}`}>
                                            <b>{pickup.driver.id} {pickup.driver.name}</b> ({pickup.driver.email})
                                        </Link>
                                        }
                                    </p>
                                    <p className="mb-0">
                                        Truck Type: <strong>{pickup.truck_type.title}</strong>
                                        {pickup.vehicle &&
                                        <span>
                                            <br/>
                                            Vehicle: <Link to={`/vehicles/${pickup.vehicle.id}`}>
                                                <b>{pickup.vehicle.id} {pickup.vehicle.make} {pickup.vehicle.model}</b>
                                            </Link>
                                        </span>}
                                    </p>
                                </div>
                            </div>
                            <ul className="list-group mb-2">
                                {shopifyFields.length > 0 && (
                                    <li className="list-group-item">
                                        <div className="mb-1">
                                            <span className="badge badge-primary">Shopify</span>
                                        </div>
                                        {shopifyFields.map((field) => (
                                            <div key={field.label}>{field.label}: <strong>{field.value}</strong></div>
                                        ))}
                                    </li>
                                )}
                                {pickup.additional_info && <li className="list-group-item">Instruction for driver: <strong>{pickup.additional_info}</strong></li>}
                                {(canEditAdminNotes || hasValue(pickup.admin_notes)) && (
                                    <li
                                        className="list-group-item"
                                        style={{
                                            borderLeft: '5px solid #ffc107',
                                            backgroundColor: '#fff8e1',
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div className="font-weight-bold">
                                                <i className="fa fa-sticky-note mr-2 text-warning" aria-hidden="true"></i>
                                                Admin Notes
                                            </div>
                                            {canEditAdminNotes && !showAdminNotesForm && (
                                                <button
                                                    className={`btn btn-sm ${hasValue(pickup.admin_notes) ? 'btn-outline-primary' : 'btn-warning'}`}
                                                    onClick={editAdminNotes}
                                                >
                                                    <i className={`fa ${hasValue(pickup.admin_notes) ? 'fa-pencil' : 'fa-plus'} mr-1`} aria-hidden="true"></i>
                                                    {hasValue(pickup.admin_notes) ? 'Edit Notes' : 'Add Notes'}
                                                </button>
                                            )}
                                        </div>
                                        {showAdminNotesForm ? (
                                            <>
                                                <textarea
                                                    id="adminNotes"
                                                    className="form-control"
                                                    rows="4"
                                                    value={adminNotes}
                                                    onChange={(event) => setAdminNotes(event.target.value)}
                                                    placeholder="Add internal admin notes for this delivery"
                                                />
                                                <div className="text-right mt-2">
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm mr-2"
                                                        onClick={cancelAdminNotesEdit}
                                                        disabled={isAdminNotesSubmitting}
                                                    >
                                                        <i className="fa fa-times mr-1" aria-hidden="true"></i>
                                                        Cancel
                                                    </button>
                                                    <button
                                                        className="btn btn-primary btn-sm"
                                                        onClick={saveAdminNotes}
                                                        disabled={isAdminNotesSubmitting || !adminNotesChanged}
                                                    >
                                                        {isAdminNotesSubmitting
                                                            ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Saving...</>
                                                            : <><i className="fa fa-save mr-1" aria-hidden="true"></i>Save Notes</>
                                                        }
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <div
                                                className={hasValue(pickup.admin_notes) ? '' : 'text-muted font-italic'}
                                                style={{whiteSpace: 'pre-wrap'}}
                                            >
                                                {hasValue(pickup.admin_notes) ? pickup.admin_notes : 'No admin notes yet.'}
                                            </div>
                                        )}
                                    </li>
                                )}
                                {pickup.claim &&
                                    <li className="list-group-item d-flex justify-content-between align-items-center">
                                        <span>Claim: <strong>{pickup.claim.status}</strong></span>
                                        &nbsp;
                                        {/* 1 - claimed, 5 - Complete, 6 - Cancelled*/}
                                        {pickup.driver && pickup.delivery_status?.id !== 5 && pickup.delivery_status?.id !== 6 &&
                                            <button className="btn btn-primary" onClick={unclaimPickup}>
                                                {isUnclaimSubmitted
                                                    ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Unclaim...</>
                                                    : "Unclaim"
                                                }
                                            </button>
                                        }
                                        {!pickup.driver && pickup.delivery_status?.id !== 5 && pickup.delivery_status?.id !== 6 &&
                                            <div className='d-flex align-items-center'>
                                                <label className="mb-0 mr-2">Driver ID:</label>
                                                <input type="number"
                                                       className="form-control text-center input-no-spinners mr-2"
                                                       placeholder="ID"
                                                       value={claimDriverId}
                                                       onChange={(e) => setClaimDriverId(e.target.value)}
                                                       style={{maxWidth: "75px", flex: '1 1 75px'}}/>
                                                <button className="btn btn-warning" onClick={claimPickup}>
                                                    {isClaimSubmitted
                                                        ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                        : "Сlaim on Behalf"
                                                    }
                                                </button>
                                            </div>
                                        }
                                    </li>
                                }
                                {pickup.cancelled_by_driver && <li className="list-group-item">
                                    Canceled by driver: <Link to={`/drivers/${pickup.cancelled_by_driver.id}`}>
                                    <b>{pickup.cancelled_by_driver.id} {pickup.cancelled_by_driver.name}</b> ({pickup.cancelled_by_driver.email})
                                </Link>
                                </li>}
                            </ul>

                            <div className="card mb-2">
                                <div className="card-header"><h5 className="mb-0 text-center">Prices</h5></div>
                                <div className="card-body">
                                    <div className="table-responsive mb-4">
                                        <table className='table table-sm table-striped text-center mb-0'>
                                            <tbody>
                                            {usedPaymentMethodLabel && (
                                                <tr>
                                                    <td>Payment Method</td>
                                                    <td><strong>{usedPaymentMethodLabel}</strong></td>
                                                </tr>
                                            )}
                                            {showRegularDeliveryCost && (
                                                <tr>
                                                    <td>Regular Delivery Fee (Delivery Cost)</td>
                                                    <td className="">${pickup.regular_delivery_cost}</td>
                                                </tr>
                                            )}
                                            {pickup.is_promo_applied && (
                                                <tr>
                                                    <td>
                                                        <Link to={`/promo-codes/${pickup.promo_code_id}`}>
                                                            Promo Discount
                                                            (
                                                            {pickup.promo_code?.discount_type === 'percentage' && <span>{pickup.promo_code?.discount_value}%</span>}
                                                            {pickup.promo_code?.discount_type === 'fixed' && <span>${pickup.promo_code?.discount_value}</span>}
                                                            )
                                                        </Link>
                                                    </td>
                                                    <td className="text-info"><b>-${pickup.discount_amount}</b></td>
                                                </tr>
                                            )}
                                            <tr>
                                                {pickup.is_promo_applied
                                                    ? <td>Delivery Fee</td>
                                                    : <td>Delivery Fee (Delivery Cost)</td>
                                                }
                                                {hasPermission("pickup-update") ? (
                                                    <td>
                                                        {pickup.delivery_status?.id === 5
                                                            ? <span>${pickup.delivery_cost}</span>
                                                            : <div className="input-group input-group-sm justify-content-center">
                                                                <div className="input-group-prepend">
                                                                    <span className="input-group-text">$</span>
                                                                </div>
                                                                <input type="number"
                                                                       className="form-control text-center input-no-spinners"
                                                                       placeholder="30.00"
                                                                       value={deliveryCost}
                                                                       onChange={(e) => setDeliveryCost(e.target.value)}
                                                                       style={{maxWidth: "75px", flex: '1 1 75px'}}/>
                                                                <div className="input-group-append">
                                                                    <button className="btn btn-outline-secondary" type="button" onClick={saveDeliveryCost}
                                                                            style={{width: '50px'}}>
                                                                        {isDeliveryCostSubmitted
                                                                            ? <><Spinner as="span" animation="border" size="sm"
                                                                                         role="status"
                                                                                         aria-hidden="true"/></>
                                                                            : "Save"
                                                                        }
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        }
                                                    </td>
                                                ) : (
                                                    <td>${pickup.delivery_cost}</td>
                                                )}
                                            </tr>
                                            <tr>
                                                <td>Cargo Protection Pkg ({pickup.cargo_insurance?.insurance_range || 'None'})</td>
                                                <td>${pickup.insurance_cost}</td>
                                            </tr>
                                            <tr>
                                                <td>Cost (Delivery Fee + Cargo Protection Pkg)</td>
                                                <td><b>${pickup.cost}</b></td>
                                            </tr>
                                            <tr>
                                                <td>GST {parseFloat(pickup.tax_rate?.gst || 0)}%</td>
                                                <td>${pickup.gst_five_percent}</td>
                                            </tr>
                                            <tr>
                                                <td>PST {parseFloat(pickup.tax_rate?.pst || 0)}%</td>
                                                <td>${pickup.pst_seven_percent}</td>
                                            </tr>
                                            <tr>
                                                <td>HST {parseFloat(pickup.tax_rate?.hst || 0)}%</td>
                                                <td>${pickup.hst_amount}</td>
                                            </tr>
                                            <tr>
                                                <td>QST {parseFloat(pickup.tax_rate?.qst || 0)}%</td>
                                                <td>${pickup.qst_amount}</td>
                                            </tr>
                                            <tr>
                                                <td>Total Cost</td>
                                                <td><b>${pickup.total_cost}</b></td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Apply Promo Code */}
                                    {!pickup.is_promo_applied && pickup.delivery_status.id !== 5 && pickup.delivery_status.id !== 6 && !pickup.waiting_for_driver_at && (
                                        <div className="mb-4 text-center">
                                            <div className="input-group input-group-sm justify-content-center" style={{maxWidth: "300px", margin: "0 auto"}}>
                                                <input type="text"
                                                       className="form-control text-center"
                                                       placeholder="Promo Code"
                                                       value={promoCode}
                                                       onChange={(e) => setPromoCode(e.target.value)}
                                                />
                                                <div className="input-group-append">
                                                    <button className="btn btn-outline-secondary" type="button" onClick={applyPromoCode}>
                                                        {isPromoSubmitting
                                                            ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true"/></>
                                                            : "Apply"
                                                        }
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <h5 className="text-center">Driver Earnings</h5>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-striped text-center mb-0">
                                            <tbody>
                                            <tr>
                                                <td>
                                                    {pickup.is_promo_applied
                                                        ? "Regular Delivery Fee (Delivery Cost)"
                                                        : "Delivery Fee (Delivery Cost)"
                                                    }
                                                </td>
                                                <td className="">${driverEarningsPickupFee}</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Link to={"/truck-types/prices#truck-type-" + pickup.truck_type.id} target="_blank">
                                                        Driver Compensation Rate ({pickup.truck_type.title})
                                                    </Link>
                                                </td>
                                                <td>{pickup.truck_type.driver_percent}%</td>
                                            </tr>
                                            <tr>
                                                <td>Driver Earnings</td>
                                                <td><b>${pickup.driver_earnings}</b></td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Billing document buttons */}
                                    {pickup.delivery_status.id === 5 && (
                                        <PickupBillingDocuments
                                            pickupId={pickup.id}
                                            token={token}
                                            receiptAvailable={pickup.receipt_available}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <GoogleMapsProvider>
                                <NPMapViewDirections
                                    origins={pickup.origins}
                                    dropoffs={pickup.dropoffs}
                                />
                            </GoogleMapsProvider>
                            <p className="mt-2">
                                Origin: <b>{formatPickupOriginDisplay(pickup)}</b>
                                <br/>
                                Dropoff: <b>{formatPickupDropoffDisplay(pickup)}</b>
                                <br/>
                                Distance: <b>{pickup.distance} km</b>
                            </p>
                            <div className="card my-2">
                                <div className="card-body">
                                    <h5 className="text-center">Timestamps</h5>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered text-center mb-0">
                                            <tbody>
                                            <tr className={pickup.scheduled_pickup_time ? 'table-info' : ''}>
                                                <td>Scheduled Delivery Time</td>
                                                <td>{formatTimestamp(pickup.scheduled_pickup_time)}</td>
                                            </tr>
                                            <tr>
                                                <td>Created At</td>
                                                <td>{formatTimestamp(pickup.created_at)}</td>
                                            </tr>
                                            <tr>
                                                <td>Waiting for Driver At</td>
                                                <td>{formatTimestamp(pickup.waiting_for_driver_at)}</td>
                                            </tr>
                                            <tr>
                                                <td>Claimed At</td>
                                                <td>{formatTimestamp(pickup.claimed_at)}</td>
                                            </tr>
                                            <tr>
                                                <td>Started At</td>
                                                <td>
                                                    <div>{formatTimestamp(pickup.started_at)}</div>
                                                    {canChangeDriverStatus && !pickup.started_at && (
                                                        <button
                                                            className="btn btn-sm btn-warning"
                                                            onClick={() => changeDriverStatus('start')}
                                                            disabled={driverStatusSubmitting === 'start'}
                                                        >
                                                            {driverStatusSubmitting === 'start'
                                                                ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Starting...</>
                                                                : 'Start Delivery on Behalf'
                                                            }
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>

                                            {trackers && trackers.map(tracker => (
                                                <tr key={tracker.id}>
                                                    <td>
                                                        {tracker.action_title} <b>{tracker.origin_items && tracker.origin_items.join(', ')} {tracker.dropoff_items && tracker.dropoff_items.join(', ')}</b>
                                                        {tracker.origin &&
                                                        <>
                                                            <br/>
                                                            <small>{formatLocationDisplay(tracker.origin)}</small>
                                                        </>
                                                        }
                                                        {tracker.dropoff &&
                                                        <>
                                                            <br/>
                                                            <small>{formatLocationDisplay(tracker.dropoff)}</small>
                                                        </>
                                                        }
                                                    </td>
                                                    <td>
                                                        {tracker.completed ? (
                                                            <>
                                                                <div>
                                                                    {moment(tracker.updated_at).format("YYYY-MM-DD HH:mm:ss")}
                                                                </div>

                                                                {canChangeDriverStatus && isAtPickupTracker(tracker) && (
                                                                    <div className="mt-2">
                                                                        <small className="d-block mb-1">
                                                                            Secured Photo <span className="text-muted">(optional)</span>
                                                                        </small>

                                                                        {tracker.origin?.photo_url ? (
                                                                            <a
                                                                                href={tracker.origin.photo_url}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="btn btn-sm btn-outline-success"
                                                                            >
                                                                                View Secured Photo
                                                                            </a>
                                                                        ) : (
                                                                            <div className="custom-file-upload-wrap">
                                                                                <input
                                                                                    type="file"
                                                                                    accept="image/jpeg,image/png,image/jpg"
                                                                                    className="form-control-file text-center"
                                                                                    disabled={photoUploadSubmitting === `secured-${getTrackerLocationId(tracker, 'secured')}`}
                                                                                    onChange={(e) => {
                                                                                        const file = e.target.files?.[0];
                                                                                        uploadAdminPickupPhoto(
                                                                                            'secured',
                                                                                            getTrackerLocationId(tracker, 'secured'),
                                                                                            file
                                                                                        );
                                                                                        e.target.value = '';
                                                                                    }}
                                                                                />

                                                                                {photoUploadSubmitting === `secured-${getTrackerLocationId(tracker, 'secured')}` && (
                                                                                    <div className="mt-1">
                                                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                                                        &nbsp;Uploading...
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {canChangeDriverStatus && isAtDropoffTracker(tracker) && (
                                                                    <div className="mt-2">
                                                                        <small className="d-block mb-1">
                                                                            Complete Delivery Photo <span className="text-danger">(required)</span>
                                                                        </small>

                                                                        {tracker.dropoff?.photo_url ? (
                                                                            <a
                                                                                href={tracker.dropoff.photo_url}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="btn btn-sm btn-outline-success"
                                                                            >
                                                                                View Complete Photo
                                                                            </a>
                                                                        ) : (
                                                                            <div className="custom-file-upload-wrap">
                                                                                <input
                                                                                    type="file"
                                                                                    accept="image/jpeg,image/png,image/jpg"
                                                                                    className="form-control-file text-center"
                                                                                    disabled={photoUploadSubmitting === `complete-${getTrackerLocationId(tracker, 'complete')}`}
                                                                                    onChange={(e) => {
                                                                                        const file = e.target.files?.[0];
                                                                                        uploadAdminPickupPhoto(
                                                                                            'complete',
                                                                                            getTrackerLocationId(tracker, 'complete'),
                                                                                            file
                                                                                        );
                                                                                        e.target.value = '';
                                                                                    }}
                                                                                />

                                                                                {photoUploadSubmitting === `complete-${getTrackerLocationId(tracker, 'complete')}` && (
                                                                                    <div className="mt-1 text-danger">
                                                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                                                        &nbsp;Uploading...
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : canChangeDriverStatus ? (
                                                            <button
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => changeDriverStatus('tracker', tracker.id)}
                                                                disabled={driverStatusSubmitting === `tracker-${tracker.id}`}
                                                            >
                                                                {driverStatusSubmitting === `tracker-${tracker.id}`
                                                                    ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Saving...</>
                                                                    : 'Mark Complete on Behalf'
                                                                }
                                                            </button>
                                                        ) : (
                                                            '---'
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}

                                            <tr className={pickup.completed_at ? 'table-success' : ''}>
                                                <td>Completed At</td>
                                                <td>
                                                    <div>{formatTimestamp(pickup.completed_at)}</div>
                                                    {canChangeDriverStatus &&
                                                        pickup.started_at &&
                                                        incompleteTrackers.length === 0 &&
                                                        !pickup.completed_at &&
                                                        !allRequiredCompletePhotosUploaded() && (
                                                        <span className="text-danger">Complete delivery photo is required for every drop-off before completing this delivery.</span>
                                                    )}
                                                    {canShowCompletePickupButton && (
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => changeDriverStatus('complete')}
                                                            disabled={driverStatusSubmitting === 'complete'}
                                                        >
                                                            {driverStatusSubmitting === 'complete'
                                                                ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Completing...</>
                                                                : 'Complete Delivery on Behalf'
                                                            }
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>Canceled At</td>
                                                <td>{formatTimestamp(pickup.canceled_at)}</td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-2">
                        <div className="card mb-2">
                            <div className="card-header">
                                <h5 className="mb-0 text-center">Items</h5>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className='table table-sm table-striped text-center mb-0'>
                                        <thead>
                                        <tr>
                                            <th>Details</th>
                                            <th>Pickup Address</th>
                                            <th>Dropoff Address</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {pickup.items.map(item => (
                                            <tr key={item.id}>
                                                <td>
                                                    <div className="list-group">
                                                        <div className="list-group-item p-0">{item.photo && <a href={item.photo} target="_blank" rel="noopener noreferrer" className="d-block p-1"><img src={item.photo} alt="Item" width="50"/></a>}</div>
                                                        <div className="list-group-item p-1"><small><b>ID: </b></small> {item.id}</div>
                                                        <div className="list-group-item p-1"><small><b>Name:</b></small> {item.item_name}</div>
                                                        <div className="list-group-item p-1"><small><b>P.O.#</b></small> {item.purchase_order_number}</div>
                                                        <div className="list-group-item p-1"><small><b>Details:</b></small> {item.details}</div>
                                                        <div className="list-group-item p-1"><small><b>Weight:</b></small> {item.weight}</div>
                                                        <div className="list-group-item p-1"><small><b>Size:</b></small> {item.size}</div>
                                                    </div>

                                                </td>
                                                <td>
                                                    {item.origin && (
                                                        <>
                                                            <b>{formatLocationDisplay(item.origin)}</b>
                                                            <br />
                                                            {item.origin.contact_name && (
                                                                <>
                                                                    {item.origin.contact_name}
                                                                    <br />
                                                                </>
                                                            )}
                                                            {item.origin.contact_phone && (
                                                                <>

                                                                    {formatPhoneNumberWithMask(item.origin.contact_phone || "",
                                                                        "+9 999 999-9999")}
                                                                    <br />
                                                                </>
                                                            )}
                                                            <div className="card p-2" align="center">
                                                                <small>Secured Photo</small> <br/> {item.origin.photo_url
                                                                ?
                                                                <a href={item.origin.photo_url} target="_blank" rel="noopener noreferrer">
                                                                    <img src={item.origin.photo_url} alt="Secured Item Photo" width="100"/>
                                                                </a>
                                                                : 'Not Uploaded'}
                                                            </div>
                                                        </>
                                                    )}
                                                </td>
                                                <td>
                                                    {item.dropoff && (
                                                        <>
                                                            <b>{formatLocationDisplay(item.dropoff)}</b>
                                                            <br />
                                                            {item.dropoff.contact_name && (
                                                                <>
                                                                    {item.dropoff.contact_name}
                                                                    <br />
                                                                </>
                                                            )}
                                                            {item.dropoff.contact_phone && (
                                                                <>

                                                                    {formatPhoneNumberWithMask(item.dropoff.contact_phone || "",
                                                                        "+9 999 999-9999")}
                                                                    <br />
                                                                </>
                                                            )}
                                                            <div className="card p-2" align="center">
                                                                <small>Complete Photo</small> <br/> {item.dropoff.photo_url
                                                                ?
                                                                <a href={item.dropoff.photo_url} target="_blank" rel="noopener noreferrer">
                                                                    <img src={item.dropoff.photo_url} alt="Complete Item Photo" width="100"/>
                                                                </a>
                                                                : 'Not Uploaded'}
                                                            </div>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Feedbacks Section */}
                    {pickup.feedbacks && (
                        <div className="mt-4">
                            <h5 className="text-center">Feedbacks</h5>
                            <div className="row">
                                {/* Feedback from Customer */}
                                <div className="col-12 col-lg-6 mb-4">
                                    <div className="card">
                                        <div className="card-body">
                                            <h6>
                                                Feedback from Customer&nbsp;
                                                {pickup.user && (
                                                    <Link to={`/users/${pickup.user.id}`}>
                                                        <b>
                                                            {pickup.user.id} {pickup.user.full_name}
                                                        </b>
                                                    </Link>
                                                )}
                                            </h6>
                                            {pickup.feedbacks.user && pickup.feedbacks.user.rating_by === 1 ? (
                                                <>
                                                    <p>
                                                        Rating:{' '}
                                                        {renderStars(
                                                            parseFloat(pickup.feedbacks.user.rate) || 0
                                                        )}
                                                    </p>
                                                    <p>Feedback: {pickup.feedbacks.user.feedback}</p>
                                                </>
                                            ) : (
                                                <p>No feedback has been left.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Feedback from Driver */}
                                <div className="col-12 col-lg-6 mb-4">
                                    <div className="card">
                                        <div className="card-body">
                                            <h6>
                                                Feedback from Driver&nbsp;
                                                {pickup.driver && (
                                                    <Link to={`/drivers/${pickup.driver.id}`}>
                                                        <b>
                                                            {pickup.driver.id} {pickup.driver.name}
                                                        </b>
                                                    </Link>
                                                )}
                                            </h6>
                                            {pickup.feedbacks.driver && pickup.feedbacks.driver.rating_by === 2 ? (
                                                <>
                                                    <p>
                                                        Rating:{' '}
                                                        {renderStars(
                                                            parseFloat(pickup.feedbacks.driver.rate) || 0
                                                        )}
                                                    </p>
                                                    <p>Feedback: {pickup.feedbacks.driver.feedback}</p>
                                                </>
                                            ) : (
                                                <p>No feedback has been left.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            )}

            <style>{`
                .table td {
                    vertical-align: middle;
                }
                .duplicate-pickup-wrap {
                    position: absolute; 
                    top: 50%;
                    left: 100%;
                    transform: translate(-100%, -55%);
                }
                @media only screen and (max-width: 768px) {
                    .duplicate-pickup-wrap {
                        position: static;
                        transform: translateX(0);
                    }
                    .duplicate-pickup-btn {
                        padding: .25rem .5rem;
                        font-size: .875rem;
                        line-height: 1.5;
                        border-radius: .2rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default withAuth(PickupDetails);
